import { SyntheticEvent, useEffect, useState } from 'react'
import { useMatch, useNavigate, useParams, useResolvedPath } from 'react-router'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import TextField from '@mui/material/TextField'
import Wrapper from '../common/Wrapper'

import { useBillingApi } from '../../api/billing/billing-context'
import { ValidatedInput } from '../common/InputWithValidation'
import { NewUser, Role, User, UserManagementSystem } from 'common-billing-server/types'
import { Path } from '../../routes/path'
import { email, MinLength, notEmpty } from '../../validators'
import { commonTextFieldProps } from '../../theme/common-styles'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import { externalRoles, isRoleAuthorized } from 'common-billing-server/role'
import { useStatefulNavigate } from '../common/hooks/hook-stateful-navigate'
import DeleteIcon from '@mui/icons-material/Delete'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import { useAuth } from '../login/Auth'
import { ActionToolbar } from 'common-ui/components/ActionToolbar'

// External users are managed from their customer, so their role is not selectable here
const selectableRoles: readonly Role[] = Object.values(Role).filter((candidate) => !externalRoles.includes(candidate))

const mfaStatusDescription = (managedBy: UserManagementSystem, totpEnabled: boolean) => {
    if (managedBy !== UserManagementSystem.billingServer) return 'Managed by Microsoft Entra ID.'
    if (totpEnabled) return 'A TOTP authenticator is enabled.'
    return 'No TOTP authenticator is configured.'
}

export const CreateEditUserPage = () => {
    const billingApi = useBillingApi()
    const { user: authenticatedUser } = useAuth()
    const { navigateBack } = useStatefulNavigate()
    const navigate = useNavigate()
    const urlParams = useParams()
    const userId: string | undefined = urlParams.userId
    const resolvedPath = useResolvedPath(Path.editUser) // Get a path template from the current URL, e.g. /users/0ff5a8e5-c373-43bd-9edd-a6b5d4933981 --> users/:userId
    const isEditingUser = !!useMatch({ path: resolvedPath.pathname, end: true }) && !!userId

    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [role, setRole] = useState(Role.invoiceOnly)
    const [managedBy, setManagedBy] = useState(UserManagementSystem.billingServer)
    const [totpEnabled, setTotpEnabled] = useState(false)
    const [resetMfaDialogOpen, setResetMfaDialogOpen] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [persistedUser, setPersistedUser] = useState<User>()
    const isUserManagedByExternalSystem = managedBy !== UserManagementSystem.billingServer
    const [isLoading, setIsLoading] = useState(false)
    const [hasSubmittedForm, setHasSubmittedForm] = useState(false)
    const [validationErrors, setValidationErrors] = useState({
        username: undefined as string | undefined,
        password: undefined as string | undefined,
    })
    const updateValidationError = (key: keyof typeof validationErrors, value: string | undefined) => {
        setValidationErrors((prevState) => ({ ...prevState, [key]: value }))
    }
    const formContainsInvalidInput = Object.values(validationErrors).some((error) => error)
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    useEffect(() => {
        const operationId = 'fetch-user'

        async function fetchUser(id: string) {
            removeFailedOperation(operationId)
            setPersistedUser(undefined)
            setIsLoading(true)
            try {
                const user = await billingApi.getUser(id)
                setUsername(user.username)
                setRole(user.role)
                setManagedBy(user.managedBy)
                setTotpEnabled(user.mfa.totpEnabled)
                setPersistedUser(user)
            } catch (error) {
                addFailedOperation({
                    id: operationId,
                    message: 'Failed fetching user',
                    error,
                })
            }
            setIsLoading(false)
        }

        if (isEditingUser) {
            void fetchUser(userId)
        }
    }, [userId, isEditingUser])

    const createOrUpdateUser = async (event: SyntheticEvent) => {
        event.preventDefault()

        const operationId = 'update-user'
        removeFailedOperation(operationId)
        setHasSubmittedForm(true)
        if (formContainsInvalidInput) {
            return
        }
        // Guards against silently reassigning the role of a user whose role has no radio (e.g. external)
        if (!selectableRoles.includes(role)) {
            addFailedOperation({
                id: operationId,
                message: `Users with role '${role}' cannot be edited here`,
            })
            return
        }
        try {
            setIsLoading(true)
            const user: NewUser = { username, password, role }
            if (isEditingUser) {
                await billingApi.updateUser(userId!, user)
            } else {
                await billingApi.createUser(user)
            }
            setIsLoading(false)
            navigateBack({ replace: true })
        } catch (error) {
            setIsLoading(false)
            addFailedOperation({
                id: operationId,
                message: `Failed ${isEditingUser ? 'updating' : 'creating'} user`,
                error,
            })
        }
    }

    const canResetMfa =
        isEditingUser &&
        totpEnabled &&
        managedBy === UserManagementSystem.billingServer &&
        !!authenticatedUser &&
        isRoleAuthorized({ role: authenticatedUser.role, minimumAllowedRole: role, strict: true })

    const resetMfa = async () => {
        if (!userId) return
        setIsLoading(true)
        try {
            await billingApi.deleteUserMfas(userId)
            setTotpEnabled(false)
            setResetMfaDialogOpen(false)
        } catch (error) {
            addFailedOperation({ id: 'reset-user-mfa', message: 'Failed resetting MFA', error })
        } finally {
            setIsLoading(false)
        }
    }

    const canDeleteUser =
        isEditingUser &&
        persistedUser?.id === userId &&
        !!authenticatedUser &&
        isRoleAuthorized({ role: authenticatedUser.role, minimumAllowedRole: Role.admin }) &&
        isRoleAuthorized({ role: authenticatedUser.role, minimumAllowedRole: persistedUser.role })

    const deleteUser = async () => {
        if (!canDeleteUser || isLoading) return
        removeFailedOperation('delete-user')
        setIsLoading(true)
        try {
            await billingApi.deleteUser(userId)
            navigate(Path.internalUsers, { replace: true })
        } catch (error) {
            addFailedOperation({ id: 'delete-user', message: 'Failed deleting user', error })
            setDeleteDialogOpen(false)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Wrapper
            title={isEditingUser ? username : undefined}
            actions={
                <ActionToolbar
                    actions={[
                        {
                            id: 'delete-user',
                            tooltip: 'Delete user',
                            icon: <DeleteIcon />,
                            onClick: () => setDeleteDialogOpen(true),
                            disabled: isLoading,
                            show: canDeleteUser,
                            iconButtonProps: { color: 'error' },
                        },
                    ]}
                />
            }
        >
            <Paper>
                <form
                    onSubmit={(e) => void createOrUpdateUser(e)}
                    style={{
                        padding: '20px',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <ValidatedInput
                        {...commonTextFieldProps()}
                        type="text"
                        label={'Email'}
                        autoFocus={true}
                        disabled={isLoading || isUserManagedByExternalSystem}
                        validators={[notEmpty, email]}
                        value={username}
                        onChange={setUsername}
                        onValidationError={(error) => updateValidationError('username', error)}
                        showErrorDespiteUntouched={hasSubmittedForm}
                    />
                    {!isUserManagedByExternalSystem && (
                        <ValidatedInput
                            {...commonTextFieldProps()}
                            type="password"
                            label={'Password'}
                            autoComplete={'new-password'}
                            disabled={isLoading || isUserManagedByExternalSystem}
                            validators={isEditingUser ? [new MinLength(8)] : [notEmpty, new MinLength(8)]}
                            value={password}
                            onChange={setPassword}
                            onValidationError={(error) => updateValidationError('password', error)}
                            showErrorDespiteUntouched={hasSubmittedForm}
                        />
                    )}

                    <TextField
                        {...commonTextFieldProps()}
                        type="text"
                        label={'Managed by'}
                        disabled={true}
                        value={managedBy}
                    />
                    <FormControl disabled={isLoading} fullWidth style={{ margin: '8px 0px 0px 0px' }}>
                        <FormLabel id="role-radio-button-group-label">Role</FormLabel>
                        <RadioGroup row aria-labelledby="row-radio-button-group-label" name="row-radio-button-group">
                            {selectableRoles.map((r) => (
                                <FormControlLabel
                                    key={r}
                                    value={r}
                                    checked={r === role}
                                    onChange={(_, checked) => {
                                        if (checked) setRole(r)
                                    }}
                                    control={<Radio />}
                                    label={r}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>

                    {isEditingUser && (
                        <Box sx={{ width: '100%', mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="h6">Authenticator app</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {mfaStatusDescription(managedBy, totpEnabled)}
                            </Typography>
                            {canResetMfa && (
                                <Button
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    sx={{ mt: 1 }}
                                    onClick={() => setResetMfaDialogOpen(true)}
                                >
                                    Reset MFA
                                </Button>
                            )}
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={formContainsInvalidInput || isLoading}
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : isEditingUser ? (
                                    <SaveIcon />
                                ) : (
                                    <AddIcon />
                                )
                            }
                        >
                            {isEditingUser ? 'Update' : 'Create User'}
                        </Button>
                    </Box>
                    {errorBanner}
                </form>
            </Paper>
            <Dialog open={resetMfaDialogOpen} onClose={() => !isLoading && setResetMfaDialogOpen(false)}>
                <DialogTitle>Reset MFA for {username}?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Their authenticator credential and active sessions will be removed. Passkeys are preserved.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetMfaDialogOpen(false)} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={() => void resetMfa()} disabled={isLoading}>
                        Reset MFA
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={deleteDialogOpen} onClose={() => !isLoading && setDeleteDialogOpen(false)}>
                <DialogTitle>Delete {persistedUser?.username}?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This permanently deletes the user and removes their access to Billing Manager.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={() => void deleteUser()} disabled={isLoading}>
                        Delete user
                    </Button>
                </DialogActions>
            </Dialog>
        </Wrapper>
    )
}
